import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'

export type LibraryType =
  | 'pose'
  | 'scene'
  | 'model'
  | 'figureScene'
  | 'modelSize'
  | 'upperGarment'
  | 'lowerGarment'
  | 'shoesBoots'
  | 'jumpsuit'
  | 'wearReference'

export interface LibraryCacheEntry {
  list: any[]
  total: number
  current: number
  pageSize: number
  currentFilter: string | number
  onlyShowCollected: boolean
  filterTabs: any[]
  filterData: any[]
  selectedItems: any[]
  figureSceneActiveTab?: 'premium' | 'set' | 'mine' | 'collect'
  figureSceneFilters?: any[]
  selectedSceneParams?: Record<string, any>
  // 模特场景库：不同一级 tab 各自的列表缓存
  figureSceneTabState?: Record<
    'premium' | 'set' | 'mine' | 'collect',
    {
      list: any[]
      total: number
      current: number
    }
  >
  // 商品场景库：当前激活的 tab
  productSceneActiveTab?: 'premium' | 'mine'
  // 商品场景库：不同一级 tab 各自的列表缓存
  productSceneTabState?: Record<
    'premium' | 'mine',
    {
      list: any[]
      total: number
      current: number
    }
  >
  revision: number
  timestamp: number
}

const createEmptyEntry = (): LibraryCacheEntry => ({
  list: [],
  total: 0,
  current: 1,
  pageSize: 24,
  currentFilter: '',
  onlyShowCollected: false,
  filterTabs: [],
  filterData: [],
  selectedItems: [],
  figureSceneActiveTab: 'premium',
  figureSceneFilters: [],
  selectedSceneParams: {},
  figureSceneTabState: undefined,
  revision: 0,
  timestamp: 0,
})

const STORAGE_KEY = 'libraryCache:v1'

const loadPersisted = (): {
  cache?: Record<LibraryType, LibraryCacheEntry>
  revision?: number
} => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed || {}
  } catch (err) {
    console.warn('libraryCache load failed', err)
    return {}
  }
}

const persistState = (cacheMap: Record<LibraryType, LibraryCacheEntry>, revision: number) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        cache: cacheMap,
        revision,
      })
    )
  } catch (err) {
    console.warn('libraryCache persist failed', err)
  }
}

export const useLibraryCacheStore = defineStore('libraryCache', () => {
  const persisted = loadPersisted()
  const cacheMap = reactive<Record<LibraryType, LibraryCacheEntry>>(
    persisted.cache || {
      pose: createEmptyEntry(),
      scene: createEmptyEntry(),
      model: createEmptyEntry(),
      figureScene: createEmptyEntry(),
      modelSize: createEmptyEntry(),
      upperGarment: createEmptyEntry(),
      lowerGarment: createEmptyEntry(),
      shoesBoots: createEmptyEntry(),
      jumpsuit: createEmptyEntry(),
      wearReference: createEmptyEntry(),
    }
  )
  const revision = ref(persisted.revision || 0)

  // 在有素材增删时调用，强制让缓存失效
  const markAssetChanged = () => {
    revision.value += 1
    persistState(cacheMap, revision.value)
  }

  const setCache = (type: LibraryType, payload: Partial<LibraryCacheEntry>) => {
    cacheMap[type] = {
      ...cacheMap[type],
      ...payload,
      revision: revision.value,
      timestamp: Date.now(),
    }
    persistState(cacheMap, revision.value)
  }

  const getCache = (type: LibraryType): LibraryCacheEntry | undefined => {
    return cacheMap[type]
  }

  const clear = (type: LibraryType) => {
    cacheMap[type] = createEmptyEntry()
    persistState(cacheMap, revision.value)
  }

  const clearAll = () => {
    ; (Object.keys(cacheMap) as LibraryType[]).forEach((key) => {
      cacheMap[key] = createEmptyEntry()
    })
    persistState(cacheMap, revision.value)
  }

  return {
    cacheMap,
    revision,
    markAssetChanged,
    setCache,
    getCache,
    clear,
    clearAll,
  }
})
